import { useCallback, useMemo } from "react";
import { useProjectStore, useContactStore, useDealStore, useTaskStore, useUserStore, useSyncStore } from "@/stores/StoreProvider";
import type { Project, ProjectRequest } from "@/types/crm";
import { toast } from "sonner";
import { toastUndo } from "@/components/ui/undo-toast";

export const PROJECTS_KEY = "projects";

export function useProjects() {
  const projectStore = useProjectStore();
  const contactStore = useContactStore();
  const dealStore = useDealStore();
  const taskStore = useTaskStore();
  const userStore = useUserStore();
  const syncStore = useSyncStore();
  const projects = projectStore.all;
  const deals = dealStore.all;
  const tasks = taskStore.all;

  // Pre-build count maps in O(deals + tasks) instead of O(projects * (deals + tasks))
  const dealCountMap = useMemo(() => {
    const map = new Map<string, number>();
    for (const d of deals) {
      if (d.projectId) map.set(d.projectId, (map.get(d.projectId) ?? 0) + 1);
    }
    return map;
  }, [deals]);

  const taskCountMap = useMemo(() => {
    const map = new Map<string, number>();
    for (const t of tasks) {
      if (t.projectId) map.set(t.projectId, (map.get(t.projectId) ?? 0) + 1);
    }
    return map;
  }, [tasks]);

  const data = useMemo(() => projects.map(p => ({
    ...p,
    contactName: p.contactId ? contactStore.getById(p.contactId)?.name : undefined,
    ownerName: p.ownerId ? userStore.getById(p.ownerId)?.displayName : undefined,
    dealsCount: dealCountMap.get(p.id) ?? 0,
    tasksCount: taskCountMap.get(p.id) ?? 0,
  })) as Project[], [projects, dealCountMap, taskCountMap]);
  // Projects are metadata (small set) — use metadataReady so the dashboard
  // doesn't wait for the full contacts/deals bootstrap to show project counts.
  return { data, isLoading: !syncStore.metadataReady };
}

export function useContactProjects(contactId: string) {
  const { data, ...rest } = useProjects();
  return { ...rest, data: contactId ? data.filter(p => p.contactId === contactId) : [] };
}

export function useProject(id: string) {
  const projectStore = useProjectStore();
  const contactStore = useContactStore();
  const dealStore = useDealStore();
  const taskStore = useTaskStore();
  const userStore = useUserStore();
  const syncStore = useSyncStore();
  const raw = id ? projectStore.getById(id) : undefined;
  const dealsForProject = id ? dealStore.getByProjectId(id) : [];
  const tasksForProject = id ? taskStore.getByProjectId(id) : [];
  const data = useMemo(() => raw ? {
    ...raw,
    contactName: raw.contactId ? contactStore.getById(raw.contactId)?.name : undefined,
    ownerName: raw.ownerId ? userStore.getById(raw.ownerId)?.displayName : undefined,
    dealsCount: dealsForProject.length,
    tasksCount: tasksForProject.length,
  } as Project : undefined, [raw, dealsForProject.length, tasksForProject.length]);
  return { data, isLoading: !data && !syncStore.bootstrapComplete };
}

export function useCreateProject() {
  const projectStore = useProjectStore();
  const create = async (data: ProjectRequest) => {
    try {
      projectStore.create(data);
      toast.success("Project created");
    } catch (error) {
      toast.error("Failed to create project");
      throw error;
    }
  };
  return { mutateAsync: create, mutate: (data: ProjectRequest) => { create(data); }, isPending: false };
}

export function useUpdateProject() {
  const projectStore = useProjectStore();
  const update = async ({ id, data }: { id: string; data: ProjectRequest }) => {
    try {
      projectStore.update(id, data);
      toast.success("Project updated");
    } catch (error) {
      toast.error("Failed to update project");
      throw error;
    }
  };
  return { mutateAsync: update, mutate: (args: { id: string; data: ProjectRequest }) => { update(args); }, isPending: false };
}

export function useDeleteProject() {
  const projectStore = useProjectStore();
  const syncStore = useSyncStore();
  const mutateAsync = useCallback(
    async ({ id, name }: { id: string; name?: string }) => {
      projectStore.delete(id);
      toastUndo(
        name ? `"${name}" deleted` : "Project deleted",
        () => {
          syncStore.cancelPendingMutation('project', 'delete', id);
          projectStore.restore(id);
        },
      );
    },
    [projectStore, syncStore]
  );
  const mutate = useCallback(
    (args: { id: string; name?: string }) => {
      void mutateAsync(args);
    },
    [mutateAsync]
  );

  return useMemo(
    () => ({ mutateAsync, mutate, isPending: false }),
    [mutateAsync, mutate]
  );
}
